import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTutorial } from '../contexts/TutorialContext';
import { useTheme } from '../contexts/ThemeContext';
import { Button } from './ui/button';
import { X, ChevronLeft, ChevronRight, Sparkles, GraduationCap } from 'lucide-react';

export function TutorialOverlay() {
  const {
    isActive,
    currentStep,
    currentStepIndex,
    totalSteps,
    totalUniqueSteps,
    nextStep,
    prevStep,
    skipTutorial,
    completeTutorial
  } = useTutorial();
  
  const { isDark } = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const [targetRect, setTargetRect] = useState(null);
  const [isTransitioning, setIsTransitioning] = useState(false);

  // Find and highlight target element
  const updateTargetPosition = useCallback(() => {
    if (!currentStep?.target) {
      setTargetRect(null);
      return;
    }

    const element = document.querySelector(currentStep.target);
    if (element) {
      const rect = element.getBoundingClientRect();
      setTargetRect({
        top: rect.top - 8,
        left: rect.left - 8,
        width: rect.width + 16,
        height: rect.height + 16
      });
    } else {
      setTargetRect(null);
    }
  }, [currentStep]);

  useEffect(() => {
    if (!isActive) return;

    updateTargetPosition();
    
    // Update position on scroll/resize
    const handleUpdate = () => updateTargetPosition();
    window.addEventListener('scroll', handleUpdate, true);
    window.addEventListener('resize', handleUpdate);
    
    // Small delay for DOM updates
    const timer = setTimeout(handleUpdate, 300);

    return () => {
      window.removeEventListener('scroll', handleUpdate, true);
      window.removeEventListener('resize', handleUpdate);
      clearTimeout(timer);
    };
  }, [isActive, currentStep, updateTargetPosition, location.pathname]);

  // Handle navigation for steps that require route changes
  useEffect(() => {
    if (!isActive || !currentStep?.route) return;
    
    if (currentStep.route && location.pathname !== currentStep.route) {
      // Don't auto-navigate, just update highlight when user navigates
    }
  }, [isActive, currentStep, location.pathname]);

  const handleNext = async () => {
    if (currentStep?.action === 'complete') {
      completeTutorial();
      return;
    }

    if (currentStep?.action === 'navigate' && currentStep?.nextRoute) {
      setIsTransitioning(true);
      navigate(currentStep.nextRoute);
      setTimeout(() => {
        nextStep();
        setIsTransitioning(false);
      }, 500);
    } else {
      nextStep();
    }
  };

  const handlePrev = () => {
    prevStep();
  };

  if (!isActive || !currentStep) return null;

  const isFirstStep = currentStepIndex === 0;
  const isLastStep = currentStepIndex === totalSteps - 1;
  const isCenterPlacement = currentStep.placement === 'center' || !targetRect;

  // Calculate tooltip position
  const getTooltipStyle = () => {
    if (isCenterPlacement || !targetRect) {
      return {
        position: 'fixed',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        maxWidth: '450px'
      };
    }

    const padding = 16;
    const tooltipWidth = 350;
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;

    switch (currentStep.placement) {
      case 'top':
        return {
          position: 'fixed',
          bottom: `${viewportHeight - targetRect.top + padding}px`,
          left: `${Math.min(Math.max(padding, targetRect.left + targetRect.width / 2 - tooltipWidth / 2), viewportWidth - tooltipWidth - padding)}px`,
          maxWidth: `${tooltipWidth}px`
        };
      case 'bottom':
        return {
          position: 'fixed',
          top: `${targetRect.top + targetRect.height + padding}px`,
          left: `${Math.min(Math.max(padding, targetRect.left + targetRect.width / 2 - tooltipWidth / 2), viewportWidth - tooltipWidth - padding)}px`,
          maxWidth: `${tooltipWidth}px`
        };
      case 'left':
        return {
          position: 'fixed',
          top: `${Math.min(Math.max(padding, targetRect.top + targetRect.height / 2 - 100), viewportHeight - 250)}px`,
          right: `${viewportWidth - targetRect.left + padding}px`,
          maxWidth: `${tooltipWidth}px`
        };
      case 'right':
      default:
        // Ensure tooltip doesn't go off-screen on right
        const leftPos = targetRect.left + targetRect.width + padding;
        const adjustedLeft = leftPos + tooltipWidth > viewportWidth 
          ? viewportWidth - tooltipWidth - padding 
          : leftPos;
        return {
          position: 'fixed',
          top: `${Math.min(Math.max(padding, targetRect.top + targetRect.height / 2 - 100), viewportHeight - 250)}px`,
          left: `${adjustedLeft}px`,
          maxWidth: `${tooltipWidth}px`
        };
    }
  };

  return (
    <>
      {/* Overlay backdrop */}
      <div 
        className="fixed inset-0 z-[9998] pointer-events-none"
        style={{
          background: isCenterPlacement 
            ? 'rgba(0, 0, 0, 0.75)' 
            : 'rgba(0, 0, 0, 0.5)'
        }}
      />

      {/* Highlight cutout for target element */}
      {targetRect && !isCenterPlacement && (
        <div
          className="fixed z-[9998] pointer-events-none"
          style={{
            top: targetRect.top,
            left: targetRect.left,
            width: targetRect.width,
            height: targetRect.height,
            boxShadow: '0 0 0 9999px rgba(0, 0, 0, 0.5)',
            borderRadius: '8px',
            border: '2px solid #10B981',
            animation: 'pulse 2s ease-in-out infinite'
          }}
        />
      )}

      {/* Tutorial tooltip */}
      <div
        className={`z-[9999] pointer-events-auto ${
          isDark ? 'bg-slate-800 border-slate-700' : 'bg-white border-slate-200'
        } rounded-xl border shadow-2xl`}
        style={getTooltipStyle()}
        data-testid="tutorial-tooltip"
      >
        {/* Header */}
        <div className={`flex items-center justify-between p-4 border-b ${
          isDark ? 'border-slate-700' : 'border-slate-200'
        }`}>
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 bg-primary/10 rounded-full flex items-center justify-center">
              <GraduationCap className="h-4 w-4 text-primary" />
            </div>
            <span className={`text-sm font-medium ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>
              Passo {currentStep.step} de {totalUniqueSteps}
            </span>
          </div>
          <button
            onClick={skipTutorial}
            className={`p-1 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors ${
              isDark ? 'text-slate-400' : 'text-slate-500'
            }`}
            data-testid="skip-tutorial-btn"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Content */}
        <div className="p-5">
          <h3 className={`text-lg font-semibold mb-3 ${isDark ? 'text-white' : 'text-slate-900'}`}>
            {currentStep.title}
          </h3>
          <p className={`text-sm leading-relaxed ${isDark ? 'text-slate-300' : 'text-slate-600'}`}>
            {currentStep.description}
          </p>
        </div>

        {/* Progress bar */}
        <div className={`px-5 pb-2`}>
          <div className={`h-1 rounded-full ${isDark ? 'bg-slate-700' : 'bg-slate-200'}`}>
            <div 
              className="h-1 bg-primary rounded-full transition-all duration-300"
              style={{ width: `${((currentStepIndex + 1) / totalSteps) * 100}%` }}
            />
          </div>
        </div>

        {/* Footer */}
        <div className={`flex items-center justify-between p-4 border-t ${
          isDark ? 'border-slate-700' : 'border-slate-200'
        }`}>
          <button
            onClick={skipTutorial}
            className={`text-sm ${isDark ? 'text-slate-400 hover:text-slate-300' : 'text-slate-500 hover:text-slate-700'}`}
            data-testid="skip-tutorial-link"
          >
            Pular tutorial
          </button>

          <div className="flex items-center gap-2">
            {!isFirstStep && (
              <Button
                variant="outline"
                size="sm"
                onClick={handlePrev}
                className={isDark ? 'border-slate-600' : ''}
                data-testid="tutorial-prev-btn"
              >
                <ChevronLeft className="h-4 w-4 mr-1" />
                Anterior
              </Button>
            )}
            <Button
              size="sm"
              onClick={handleNext}
              disabled={isTransitioning}
              className="bg-primary hover:bg-primary-hover"
              data-testid="tutorial-next-btn"
            >
              {isLastStep ? (
                <>
                  <Sparkles className="h-4 w-4 mr-1" />
                  Concluir
                </>
              ) : (
                <>
                  Próximo
                  <ChevronRight className="h-4 w-4 ml-1" />
                </>
              )}
            </Button>
          </div>
        </div>
      </div>

      {/* Pulse animation style */}
      <style>{`
        @keyframes pulse {
          0%, 100% {
            box-shadow: 0 0 0 9999px rgba(0, 0, 0, 0.5), 0 0 0 0 rgba(16, 185, 129, 0.4);
          }
          50% {
            box-shadow: 0 0 0 9999px rgba(0, 0, 0, 0.5), 0 0 0 8px rgba(16, 185, 129, 0.1);
          }
        }
      `}</style>
    </>
  );
}
